import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { TestBem } from '../../../service/TestBem';
import {
    EditorDemographyRateCorrectionCondition,
    RateControlValue,
    RateCorrectionAge,
    RateCorrectionConditionId,
    RateCorrectionGender,
    RateCorrectionType
} from '../../../typings/ratesCorrections';
import { RadioButtonDecorator, RadioItemDecorator } from '../../RadioButtonDecorator/RadioButtonDecorator';
import { RateControl } from '../../RateControl/RateControl';
import { SelectDecorator } from '../../SelectDecorator/SelectDecorator';

import './RatesCorrectionsListDemographyCondition.css';
import * as keyset from './RatesCorrectionsListDemographyCondition.i18n';

export type onChangeDemographyConditionParams = {
    type: RateCorrectionType.demography;
    value: EditorDemographyRateCorrectionCondition;
    id: RateCorrectionConditionId;
};

export interface RatesCorrectionsListDemographyConditionProps {
    value: EditorDemographyRateCorrectionCondition;
    index: number;
    maxRate?: number;
    minRate?: number;
    isDisabled: boolean;
    isAllGendersDisabled: boolean;
    isAllAgesDisabled: boolean;
    onChange: (params: onChangeDemographyConditionParams) => void;
}

const blockClass = 'rates-corrections-list-demography-condition';

const i18n = i18nFactory(keyset);

export class RatesCorrectionsListDemographyCondition extends React.PureComponent<
    RatesCorrectionsListDemographyConditionProps
> {
    render() {
        const { isDisabled, value, minRate, maxRate, isAllGendersDisabled, isAllAgesDisabled, index } = this.props;
        const ageSelectorItems = [
            RateCorrectionAge.all,
            RateCorrectionAge.age0_17,
            RateCorrectionAge.age18_24,
            RateCorrectionAge.age25_34,
            RateCorrectionAge.age35_44,
            RateCorrectionAge.age45_54,
            RateCorrectionAge.age55_
        ];

        return (
            <div className={blockClass}>
                <RadioButtonDecorator
                    disabled={isDisabled}
                    size={'s'}
                    view={'classic'}
                    theme={'normal'}
                    value={value.gender}
                    onChange={this.onGenderChange}
                    valid={value.errors.gender === undefined || value.errors.gender.length === 0}
                >
                    <RadioItemDecorator
                        cls={TestBem.cls(blockClass, 'gender', {
                            [RateCorrectionGender.all]: true
                        })}
                        controlAttrs={{
                            title: i18n('all-genders')
                        }}
                        value={RateCorrectionGender.all}
                        disabled={isAllGendersDisabled}
                    >
                        {i18n('all-genders')}
                    </RadioItemDecorator>

                    <RadioItemDecorator
                        cls={TestBem.cls(blockClass, 'gender', {
                            [RateCorrectionGender.male]: true
                        })}
                        controlAttrs={{
                            title: i18n('male')
                        }}
                        value={RateCorrectionGender.male}
                    >
                        {i18n('male')}
                    </RadioItemDecorator>

                    <RadioItemDecorator
                        cls={TestBem.cls(blockClass, 'gender', {
                            [RateCorrectionGender.female]: true
                        })}
                        controlAttrs={{
                            title: i18n('female')
                        }}
                        value={RateCorrectionGender.female}
                    >
                        {i18n('female')}
                    </RadioItemDecorator>
                </RadioButtonDecorator>

                <SelectDecorator
                    disabled={isDisabled}
                    cls={classNames(Bem.cls(blockClass, 'age'), TestBem.cls(blockClass, 'age-select'))}
                    placeholder={i18n('age')}
                    view={'classic'}
                    type={'radio'}
                    theme={'normal'}
                    size={'s'}
                    onChange={this.onAgeChange}
                    items={ageSelectorItems.map(item => {
                        return {
                            cls: TestBem.cls(blockClass, 'age-item', {
                                [item]: true,
                                index: `${index + 1}`
                            }),
                            val: item,
                            text: rateCorrectionAgeToText[item],
                            disabled: item === RateCorrectionAge.all && isAllAgesDisabled
                        };
                    })}
                    valid={value.errors.age === undefined || value.errors.age.length === 0}
                    val={value.age}
                />

                <RateControl
                    value={value.value}
                    isDisabled={isDisabled}
                    cls={Bem.cls(blockClass, 'rate')}
                    isCommon={false}
                    max={maxRate}
                    min={minRate}
                    errors={value.errors.value}
                    onChange={this.onRateControlChange}
                />
            </div>
        );
    }

    private onAgeChange = (ageValue: Array<RateCorrectionAge>) => {
        const { value, onChange } = this.props;

        onChange({
            type: RateCorrectionType.demography,
            id: value.id,
            value: {
                ...value,
                age: ageValue[0]
            }
        });
    };

    private onGenderChange = (event: React.FormEvent<HTMLInputElement>) => {
        const { value, onChange } = this.props;

        onChange({
            type: RateCorrectionType.demography,
            id: value.id,
            value: {
                ...value,
                gender: event.currentTarget.value as RateCorrectionGender
            }
        });
    };

    private onRateControlChange = (rate: RateControlValue) => {
        const { value, onChange } = this.props;

        onChange({
            type: RateCorrectionType.demography,
            id: value.id,
            value: {
                ...value,
                value: rate
            }
        });
    };
}

const rateCorrectionAgeToText: Record<RateCorrectionAge, string> = {
    [RateCorrectionAge.all]: i18n('all-ages'),
    [RateCorrectionAge.age0_17]: i18n('0-17'),
    [RateCorrectionAge.age18_24]: i18n('18-24'),
    [RateCorrectionAge.age25_34]: i18n('25-34'),
    [RateCorrectionAge.age35_44]: i18n('35-44'),
    [RateCorrectionAge.age45_54]: i18n('45-54'),
    [RateCorrectionAge.age55_]: i18n('55-')
};
